<template>
  <div>
    <v-form
      class="bg-light py-md-3 py-1 px-3 rounded mb-3"
      @submit.prevent="submit()"
    >
      <div class="row">
        <div class="col-2">
          <div v-if="productPackage.id" class="mb-2">
            ID: {{ productPackage.id }}
          </div>
          <div class="mb-2">
            <v-select
              v-model="productPackage.class_category_id"
              :items="lookup.package_categories"
              bg-color="white"
              density="compact"
              item-title="name"
              item-value="id"
              variant="outlined"
            />
          </div>
          <div class="pb-0">
            <v-text-field
              v-model="productPackage.name"
              bg-color="white"
              variant="outlined"
              density="compact"
              label="Name*"
              :error-messages="
                v$.productPackage.name.$errors.map((e) => e.$message)
              "
              @blur="v$.productPackage.name.$touch()"
            />
          </div>
          <div class="pb-0">
            <v-text-field
              v-model="productPackage.description"
              bg-color="white"
              variant="outlined"
              density="compact"
              label="Description"
              :error-messages="
                v$.productPackage.description.$errors.map((e) => e.$message)
              "
              @blur="v$.productPackage.description.$touch()"
            />
          </div>
          <div>
            <button
              v-if="productPackage.id"
              type="button"
              class="btn btn-default btn-form-group p-2 mr-2"
              :disabled="saveInProgress"
              @click="save"
            >
              Save
              <span
                v-show="saveInProgress"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="productPackage.id"
              type="button"
              class="btn btn-default btn-form-group p-2"
              :disabled="deleteInProgress"
              @click="remove"
            >
              Delete
              <span
                v-show="deleteInProgress"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            </button>
            <button
              v-if="!productPackage.id"
              type="button"
              class="btn btn-default btn-form-group p-2"
              :disabled="addInProgress"
              @click="add"
            >
              Add
              <span
                v-show="addInProgress"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <div class="col-2">
          <div class="mb-2">
            <strong>Included Products:</strong>
          </div>

          <table
            class="table table-striped table-hover table-sm table-bordered mb-2"
          >
            <tbody>
              <tr
                v-for="includedProduct in productPackage.included_products"
                :key="includedProduct.id"
              >
                <td>{{ includedProduct.name }}</td>
                <td>{{ toCurrency(includedProduct.unit_price) }}</td>
                <td>
                  <a
                    href="#"
                    tabindex="0"
                    class="font-icon-delete h5 pl-2 mb-0"
                    @click.prevent="removeIncludedProduct(includedProduct)"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div class="mb-3">
            <strong>Product Total:</strong>
            {{ toCurrency(totalIncudedProducts) }}
          </div>

          <div>
            <v-autocomplete
              bg-color="white"
              item-title="name"
              item-value="id"
              name="product"
              variant="outlined"
              :items="products"
              placeholder="Select new product to Add"
              @update:model-value="addIncludedProduct"
            />
          </div>

          <v-checkbox v-model="productPackage.buyable" label="Can buy" />
        </div>

        <div v-if="productPackage.id" class="col-2">
          <div class="mb-2">
            <strong>Discounts:</strong>
            <a
              href="#"
              tabindex="0"
              class="font-icon-edit h5 pl-2"
              @click.prevent="isDiscountsEditModalOpen = true"
            />
          </div>

          <div class="row">
            <div class="col-12">
              <table
                v-if="productPackage.package_discounts.length"
                class="table table-striped table-hover table-sm table-bordered"
              >
                <thead>
                  <th>Quantity more than</th>
                  <th>Discount amount</th>
                  <th>Package Total</th>
                </thead>
                <tbody>
                  <tr
                    v-for="packageDiscount in productPackage.package_discounts"
                    :key="packageDiscount.id"
                  >
                    <td>{{ packageDiscount.cutoff_count }}</td>
                    <td>{{ toCurrency(packageDiscount.amount) }}</td>
                    <td>
                      {{
                        toCurrency(
                          totalIncudedProducts - packageDiscount.amount
                        )
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12 d-flex flex-column">
              <v-text-field
                v-model="productPackage.retail_price"
                bg-color="white"
                variant="outlined"
                density="compact"
                label="Retail Price"
                type="number"
              />
              <v-text-field
                v-model="productPackage.promotion_text"
                bg-color="white"
                variant="outlined"
                density="compact"
                label="Promotional Text"
              />
              <v-checkbox
                v-model="productPackage.is_best_value"
                label="Best Value Badge"
              />
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12 text-right">
              <button
                type="button"
                class="btn btn-default btn-form-group p-2 mr-2"
                :disabled="saveInProgress"
                @click="save"
              >
                Save
                <span
                  v-show="saveInProgress"
                  class="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>

        <div class="col-2">
          <div class="mb-2">
            <strong>
              Optional Products:
            </strong>
            <v-tooltip
              width="300px"
              activator="parent"
              location="bottom start"
              location-strategy="connected"
            >
              Offered on Servsafe public registration form only
              <template #activator>
                <v-icon icon="mdi:mdi-information-outline" size="x-small" />
              </template>
            </v-tooltip>
          </div>

          <table
            class="table table-striped table-hover table-sm table-bordered"
          >
            <tbody>
              <tr
                v-for="optionalProduct in productPackage.optional_products"
                :key="optionalProduct.id"
              >
                <td>{{ optionalProduct.name }}</td>
                <td>{{ toCurrency(optionalProduct.unit_price) }}</td>
                <td>
                  <a
                    href="#"
                    tabindex="0"
                    class="font-icon-delete h5 pl-2 mb-0"
                    @click.prevent="removeOptionalProduct(optionalProduct)"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div>
            <v-autocomplete
              bg-color="white"
              item-value="id"
              item-title="name"
              :items="products"
              variant="outlined"
              placeholder="Select new product to Add"
              @update:model-value="addOptionalProduct"
            />
          </div>
        </div>

        <div :class="isDialogView ? 'col-4' : 'col-2'">
          <div class="mb-2">
            <strong>
              Expenses (per package):
              <v-tooltip
                width="300px"
                activator="parent"
                location="bottom start"
                location-strategy="connected"
              >Amount subtracted from order total. Reported values update
                automatically on GTM.
                <template #activator>
                  <v-icon icon="mdi:mdi-information-outline" size="x-small" />
                </template>
              </v-tooltip>
            </strong>
          </div>

          <div class="form-row">
            <div class="col-7">
              <div class="pb-0">
                <v-textarea
                  v-model="productPackage.expenses_description"
                  bg-color="white"
                  rows="4"
                  label="Description"
                  variant="outlined"
                  density="compact"
                  :error-messages="
                    v$.productPackage.expenses_description.$errors.map(
                      (e) => e.$message
                    )
                  "
                  @blur="v$.productPackage.expenses_description.$touch()"
                />
              </div>
            </div>
            <div class="col-5">
              <div class="pb-0">
                <v-text-field
                  v-model="productPackage.expenses_amount"
                  bg-color="white"
                  variant="outlined"
                  density="compact"
                  name="expenses_amount"
                  type="number"
                  step="0.01"
                  label="Amount"
                  :error-messages="
                    v$.productPackage.expenses_amount.$errors.map(
                      (e) => e.$message
                    )
                  "
                  @blur="v$.productPackage.expenses_amount.$touch()"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="col-2">
          <div class="mb-2">
            <strong>
              States Offered:
              <v-tooltip
                width="300px"
                activator="parent"
                location="bottom start"
                location-strategy="connected"
              >Used in internal system. Defines which packages are available
                for selected state.
                <template #activator>
                  <v-icon icon="mdi:mdi-information-outline" size="x-small" />
                </template>
              </v-tooltip>
            </strong>
          </div>

          <v-autocomplete
            v-model="stateValues"
            closable-chips
            class="state"
            bg-color="white"
            chips
            name="state_code"
            variant="outlined"
            item-value="state_code"
            item-title="name"
            :items="lookup.states"
            :multiple="true"
          />
        </div>
      </div>
    </v-form>

    <v-dialog
      v-model="isDiscountsEditModalOpen"
      max-width="1140px"
    >
      <v-card>
        <template #title>
          {{ productPackage.name + ' Package Discounts' }}
        </template>
        <v-divider />
        <div class="px-3 pb-4">
          <package-discount
            :package_id="productPackage.id"
            :total-incuded-products-value="totalIncudedProducts"
            @add-discount="onAddDiscount"
          />

          <package-discount
            v-for="(package_discount, index) in productPackage.package_discounts"
            :key="package_discount.id"
            :value="productPackage.package_discounts[index]"
            :index="index"
            :package_id="productPackage.id"
            :total-incuded-products-value="totalIncudedProducts"
            @save-discount="onSaveDiscount"
            @remove-discount="onRemoveDiscount"
          />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import axios from 'axios';
import { appValidator } from '../../common/core/utils/validator';
import { lookup } from '../lookup/lookup';

import { toCurrency } from '../../helpers/currency';

import ProductPackageDiscount from './productPackageDiscount.vue';

export default {
  components: {
    'package-discount': ProductPackageDiscount,
  },
  props: ['value', 'products', 'isDialogView'],
  setup() {
    return { v$: useVuelidate(), toCurrency };
  },
  data() {
    return {
      productPackage: { ...this.value },
      lookup,
      saveInProgress: false,
      addInProgress: false,
      deleteInProgress: false,
      includedProductsValue: [],
      stateValues: [],
      isDiscountsEditModalOpen: false,
    };
  },

  computed: {
    totalIncudedProducts() {
      let total = 0;
      if (this.productPackage.included_products) {
        this.productPackage.included_products.forEach(includedProduct => {
          total += includedProduct.unit_price;
        });
      }
      return total;
    },
  },

  watch: {
    stateValues(newStateCodeValues) {
      this.productPackage.states = newStateCodeValues.map(stateCode => this.lookup.states.find(s => s.state_code === stateCode));
    },
  },

  mounted() {
    this.stateValues = this.productPackage.states.map(s => s.state_code);
    this.sortDiscounts();
  },

  validations() {
    return {
      productPackage: {
        name: {
          required: appValidator.required,
        },
        description: {},
        expenses_description: {},
        expenses_amount: {},
      },
    };
  },

  methods: {
    validatePackage() {
      let error = false;
      if (this.v$.productPackage.$invalid) {
        this.v$.productPackage.$touch();
        error = true;
      }
      return !error;
    },

    addIncludedProduct(productId) {
      const product = this.products.find(p => p.id === productId);
      this.productPackage.included_products.push(product);
    },

    removeIncludedProduct(product) {
      this.productPackage.included_products.splice(
        this.productPackage.included_products.indexOf(product),
        1,
      );
    },

    addOptionalProduct(productId) {
      const product = this.products.find(p => p.id === productId);
      this.productPackage.optional_products.push(product);
    },

    removeOptionalProduct(product) {
      this.productPackage.optional_products.splice(
        this.productPackage.optional_products.indexOf(product),
        1,
      );
    },

    sortDiscounts() {
      this.productPackage.package_discounts.sort(
        (a, b) => a.cutoff_count - b.cutoff_count,
      );
    },
    onAddDiscount(discount) {
      this.productPackage.package_discounts.push(discount);
      this.sortDiscounts();
    },

    onRemoveDiscount(index) {
      this.productPackage.package_discounts.splice(index, 1);
    },

    onSaveDiscount(index, discount) {
      this.productPackage.package_discounts[index] = discount;
      this.sortDiscounts();
    },

    async save() {
      if (!this.validatePackage()) {
        return;
      }

      this.saveInProgress = true;

      await axios.put(
        `/api/packages/${this.productPackage.id}`,
        this.productPackage,
      );

      this.saveInProgress = false;

      this.$emit('save-package', this.productPackage);
    },

    async add() {
      if (!this.validatePackage()) {
        return;
      }

      this.addInProgress = true;

      const response = await axios.post('/api/packages', this.productPackage);
      this.productPackage.id = response.data.data.id;

      this.addInProgress = false;

      this.$emit('add-package', this.productPackage);
    },
    async remove() {
      if (confirm('Do you really want to delete?')) {
        this.deleteInProgress = true;

        const response = await axios.delete(
          `/api/packages/${this.productPackage.id}`,
          this.productPackage,
        );

        this.deleteInProgress = false;

        this.$emit('remove-package', this.productPackage);
      }
    },
  },
};
</script>
<style scoped>
.state :deep(.v-chip) {
  /** Use this color to have as same color as legacy page */
  background-color: #41B883;
  color: white;
}
</style>
